/**
 * Opens the before/after overview of a finished rewrite (DESIGN UD-22). "Before" and "after" are full analyses through the
 * same plain-text pipeline a newly loaded document goes through: "before" of the working text the run started from
 * (`baseText`, else, for a job from before it was kept, the open document's text, which is the document the job was run on:
 * `openJob` refuses any other), "after" of the rewritten text (`resultText`). The open document's own analysis is not used as
 * "before": it reads the original file's formatting, so a text the run left unchanged would show changes of several points.
 * Each runs in a worker of its own so the open document is not replaced or queued behind it (`summary-client.ts`). Nothing is
 * approximated from Tier C. Both summaries are stored in the job record, so opening the job again is instant. The analysis of
 * a long text is shown as work with a time estimate (UD-11): the status bar's indicator, and the overview's own progress line.
 *
 * Since the version chain (DESIGN UD-27, P3), "before" is always version 0, the original document, whichever version the job
 * started from: the plain-text summary of version 0's working text (the bench worker's fixed point, which is a job's
 * `baseText` when it started from version 0). It is measured once per document and kept in the chain (`DocChain.origin`).
 */
import type { AnalysisSummary } from '../../shared/summary.ts';
import { analysisPriorMs, recordAnalysisRate } from '../rates.ts';
import { result as analysisResult } from '../state.ts';
import { OVERVIEW } from '../strings-bench.ts';
import { beginWork, endWork, updateWork } from '../work.ts';
import { keepOrigin, origin } from './chain-control.ts';
import { baseOf } from './chain.ts';
import type { BenchJob } from './job.ts';
import { faultReason } from './messages.ts';
import {
	chain,
	jobs,
	jobsElsewhere,
	liveJobId,
	overview,
	overviewError,
	overviewOpen,
	overviewProgress,
} from './state.ts';
import { getJob, listJobsDetailed, putJob } from './store.ts';
import { summariseInWorker } from './summary-client.ts';

let measuring: AbortController | null = null;

/** Ends a measurement in progress (the customer cancelled, or another job's overview is opening). */
export function cancelMeasure(): void {
	measuring?.abort();
	measuring = null;
}

/**
 * The text "before" (version 0) is measured on, and whether it still has to be taken to its working text: a job that started
 * from version 0 holds it (`baseText`); otherwise the upload's text when the open document is the job's.
 */
function beforeText(job: BenchJob): { text: string; working: boolean } | null {
	if (baseOf(job).n === 0 && job.baseText) return { text: job.baseText, working: false };
	const o = origin() ?? analysisResult.value;
	const root = o ? (o.meta.version?.root ?? o.meta.docHash) : null;
	if (!o || o.meta.version || root !== job.docHash) return null;
	return { text: o.text, working: true };
}

/** Version 0's summary when the chain already holds it (a job on version 0 that kept `before` gives it to the chain). */
function originSummary(job: BenchJob): AnalysisSummary | null {
	const c = chain.peek();
	if (c && c.root === job.docHash && c.origin) return c.origin;
	return baseOf(job).n === 0 ? (job.before ?? null) : null;
}

/** Writes the summaries into the stored record (re-read first: only these two fields change) and refreshes the list. */
async function keepSummaries(
	id: string,
	patch: { before?: AnalysisSummary; after?: AnalysisSummary }
): Promise<void> {
	const latest = await getJob(id);
	if (!latest) return;
	await putJob({ ...latest, ...patch });
	const list = await listJobsDetailed(liveJobId.peek());
	jobs.value = list.jobs;
	jobsElsewhere.value = list.elsewhere;
}

/** Opens the overview of a finished job, measuring whatever of "before" and "after" the record does not hold yet. */
export async function showOverview(job: BenchJob): Promise<void> {
	cancelMeasure();
	const fresh = (await getJob(job.id)) ?? job;
	const known = originSummary(fresh);
	if (known) void keepOrigin(fresh.docHash, known);
	const data = {
		job: fresh,
		before: known,
		after: fresh.after ?? null,
		over: fresh.report?.tierC?.over ?? [],
	};
	overview.value = data;
	overviewError.value = null;
	overviewProgress.value = null;
	overviewOpen.value = true;
	if (data.before && data.after) return;

	const controller = new AbortController();
	measuring = controller;
	try {
		const after0 = fresh.resultText ?? fresh.report?.text;
		if (after0 === undefined && data.after === null) {
			overviewError.value = OVERVIEW.noAfter;
			return;
		}
		const base = data.before ? null : beforeText(fresh);
		// the measurements still to make, in order; each is one share of the progress line
		const steps: Array<{ side: 'before' | 'after'; text: string; working?: boolean }> = [
			...(base !== null
				? [{ side: 'before' as const, text: base.text, working: base.working }]
				: []),
			...(data.after === null && after0 !== undefined
				? [{ side: 'after' as const, text: after0 }]
				: []),
		];
		const words = fresh.words ?? data.before?.words ?? (after0 ?? '').split(/\s+/).length;
		const one = analysisPriorMs(words);
		const prior = one === null ? null : one * steps.length;
		const t0 = performance.now();
		overviewProgress.value = {
			startedAt: t0,
			priorMs: prior,
			fraction: 0,
			side: steps[0]?.side ?? 'after',
		};
		beginWork('overview', OVERVIEW.measuring, { priorMs: prior, fraction: 0 });
		const got: { before?: AnalysisSummary; after?: AnalysisSummary } = {};
		try {
			for (const [i, step] of steps.entries()) {
				const s0 = performance.now();
				const summary = await summariseInWorker(
					step.text,
					(f) => {
						const fraction = (i + f) / steps.length;
						const p = overviewProgress.peek();
						if (p) overviewProgress.value = { ...p, fraction, side: step.side };
						updateWork('overview', { fraction });
					},
					controller.signal,
					step.working ? { working: true } : undefined
				);
				recordAnalysisRate(summary.words || words, performance.now() - s0);
				got[step.side] = summary;
				if (step.side === 'before') await keepOrigin(fresh.docHash, summary);
				const shown = overview.peek();
				if (shown && shown.job.id === fresh.id) overview.value = { ...shown, ...got };
				await keepSummaries(fresh.id, got);
			}
		} finally {
			endWork('overview');
			overviewProgress.value = null;
		}
		if (!data.after && !got.after) overviewError.value = OVERVIEW.noAfter;
	} catch (e) {
		if (controller.signal.aborted) return;
		overviewError.value = OVERVIEW.failed(faultReason(e));
	} finally {
		if (measuring === controller) measuring = null;
	}
}

export function closeOverview(): void {
	overviewOpen.value = false;
}

/** Measure again after a failure. */
export function retryOverview(): void {
	const shown = overview.peek();
	if (shown) void showOverview(shown.job);
}
